import { metadata, version } from "../../package.json";
import { SDKConfiguration } from "./shared/configuration";
import { isSolanaStakingError } from "./shared/error";
import { getLogger } from "./shared/logger";
import { SolanaStakingSdk } from "./solana-staking-sdk";
/**
 * Current version of the Solana Staking SDK.
 *
 * @remarks
 * This version is automatically synchronized with the package.json version
 * and is used throughout the SDK for version reporting and compatibility checks.
 *
 * @example
 * ```typescript
 * import { SDK_VERSION } from "@blockdaemon/solana-staking-sdk";
 * console.log(`Solana Staking SDK Version: ${SDK_VERSION}`);
 * ```
 */
export const SDK_VERSION = version;
/**
 * Solana Staking API version string for the SDK.
 *
 * @remarks
 * This constant provides the version identifier for the Solana staking API
 * used by the SDK. It's useful for API compatibility checks and debugging.
 *
 * @example
 * ```typescript
 * import { SOLANA_STAKING_API_VERSION } from "@blockdaemon/solana-staking-sdk";
 * console.log(`Solana Staking API Version: ${SOLANA_STAKING_API_VERSION}`);
 * ```
 */
export const SOLANA_STAKING_API_VERSION = metadata.SOLANA_STAKING_API_VERSION;
/**
 * Main Solana Staking SDK class - the primary entry point for all staking operations.
 *
 * @remarks
 * This is the main class that provides access to all SDK functionality including
 * staking operations, validator management, and transaction utilities.
 *
 * @example
 * ```typescript
 * import SolanaStakingSdk from "@blockdaemon/solana-staking-sdk";
 *
 * const sdk = new SolanaStakingSdk({
 *   rpcEndpoint: "https://api.mainnet-beta.solana.com",
 *   logLevel: "INFO"
 * });
 *
 * // Use the SDK to build a stake transaction
 * const result = await sdk.solanaStaking.buildStakeTransaction({
 *   payerPubkey: "...",
 *   amount: 1_000_000_000,
 *   validatorVotePubkey: "...",
 * });
 * ```
 */
export { SDKConfiguration, SolanaStakingSdk, getLogger, isSolanaStakingError };
export { SolanaValidatorError } from "./shared/error";
export { txDecode, txEncode } from "./shared/tx";
/**
 * Default export of the main Solana Staking SDK class.
 *
 * @remarks
 * This allows for both named and default imports of the SDK:
 * - Default: `import SolanaStakingSdk from "@blockdaemon/solana-staking-sdk"`
 * - Named: `import { SolanaStakingSdk } from "@blockdaemon/solana-staking-sdk"`
 *
 * @example
 * ```typescript
 * // Default import (recommended)
 * import SolanaStakingSdk from "@blockdaemon/solana-staking-sdk";
 *
 * // Named import
 * import { SolanaStakingSdk } from "@blockdaemon/solana-staking-sdk";
 * ```
 */
export default SolanaStakingSdk;
// Usage examples:
// 1. Direct type import:
// import { IStakeAccountInfo } from "@blockdaemon/solana-staking-sdk";
// const stakeAccount: IStakeAccountInfo = { ... };
//
// 2. Default import:
// import SolanaStakingSdk from "@blockdaemon/solana-staking-sdk";
// const sdk = new SolanaStakingSdk({ rpcEndpoint: "https://api.mainnet-beta.solana.com" });
//
// 3. Logger usage:
// import { getLogger } from "@blockdaemon/solana-staking-sdk";
// const logger = getLogger({ label: "my-app", level: "info" });
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoicHVibGljLWFwaS5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uLy4uL3NyYy9tYWluL3B1YmxpYy1hcGkudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUEsT0FBTyxFQUFFLFFBQVEsRUFBRSxPQUFPLEVBQUUsTUFBTSxvQkFBb0IsQ0FBQztBQUN2RCxPQUFPLEVBQUUsZ0JBQWdCLEVBQUUsTUFBTSx3QkFBd0IsQ0FBQztBQUUxRCxPQUFPLEVBQUUsb0JBQW9CLEVBQUUsTUFBTSxnQkFBZ0IsQ0FBQztBQUN0RCxPQUFPLEVBQUUsU0FBUyxFQUFtRCxNQUFNLGlCQUFpQixDQUFDO0FBRTdGLE9BQU8sRUFBRSxnQkFBZ0IsRUFBRSxNQUFNLHNCQUFzQixDQUFDO0FBdUZ4RDs7Ozs7Ozs7Ozs7O0dBWUc7QUFDSCxNQUFNLENBQUMsTUFBTSxXQUFXLEdBQUcsT0FBTyxDQUFDO0FBRW5DOzs7Ozs7Ozs7Ozs7R0FZRztBQUNILE1BQU0sQ0FBQyxNQUFNLDBCQUEwQixHQUFHLFFBQVEsQ0FBQywwQkFBMEIsQ0FBQztBQUU5RTs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7R0F1Qkc7QUFDSCxPQUFPLEVBQUUsZ0JBQWdCLEVBQUUsZ0JBQWdCLEVBQUUsU0FBUyxFQUFFLG9CQUFvQixFQUFFLENBQUM7QUFHL0UsT0FBTyxFQUFFLG9CQUFvQixFQUFFLE1BQU0sZ0JBQWdCLENBQUM7QUFDdEQsT0FBTyxFQUFFLFFBQVEsRUFBRSxRQUFRLEVBQUUsTUFBTSxhQUFhLENBQUM7QUFFakQ7Ozs7Ozs7Ozs7Ozs7Ozs7R0FnQkc7QUFDSCxlQUFlLGdCQUFnQixDQUFDO0FBRWhDLGtCQUFrQjtBQUNsQix5QkFBeUI7QUFDekIsdUVBQXVFO0FBQ3ZFLG1EQUFtRDtBQUNuRCxFQUFFO0FBQ0YscUJBQXFCO0FBQ3JCLGtFQUFrRTtBQUNsRSw0RkFBNEY7QUFDNUYsRUFBRTtBQUNGLG1CQUFtQjtBQUNuQiwrREFBK0Q7QUFDL0QsZ0VBQWdFIn0=